import Image from "../fragments/Image";
import SpecialText from '../fragments/SpecialText';
import SmartLink from '../fragments/SmartLink';
import { useReveal } from '../hooks/UseReveal';
import { useState, useEffect, useRef } from "react";

export default function Media({ block }) {

    const [blockLoaded, setBlockLoaded] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const parallaxRef = useRef(null);
    const elementOffsetTopRef = useRef(null);

    useEffect(() => {
        setBlockLoaded(true);
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (!isMounted || !parallaxRef.current) return;

        const parallaxElement = parallaxRef.current;
        const speed = 0.5;

        // Calculate true offset from document top by recursing through offsetParent
        const calculateOffset = () => {
            let offset = 0;
            let element = parallaxElement;
            while (element) {
                offset += element.offsetTop;
                element = element.offsetParent;
            }
            elementOffsetTopRef.current = offset;
        };

        const handleScroll = () => {
            if (elementOffsetTopRef.current === null) return;
            const scrollPos = window.lenis?.scroll || window.scrollY || 0;
            const offset = (scrollPos - elementOffsetTopRef.current) * speed;
            parallaxElement.style.transform = `translate3d(0, ${offset}px, 0)`;
        };

        const handleResize = () => {
            calculateOffset();
            handleScroll();
        };

        // Initial calculation
        requestAnimationFrame(() => {
            calculateOffset();
            handleScroll();
        });

        // Listen to Lenis scroll event
        window.lenis?.on('scroll', handleScroll);
        // Fallback for regular scroll
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);

        return () => {
            window.lenis?.off('scroll', handleScroll);
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [isMounted]);

    const textRevealRef = useReveal({ once: true });
    const buttonsRevealRef = useReveal({ once: true });
    
    return (
        <div id={block.section_id} className={`c-estimate-one-splash c-page__block c-page__block--splash`}>
            <div className="c-estimate-one-splash__media">
				<div className="c-image-parallax" ref={parallaxRef}>
                    <Image 
                        src={block.img_src} 
                        alt={block.img_alt}
                    />
                </div>
            </div>
            <div className={`l-grid c-estimate-one-splash__content c-estimate-one-splash__content--top`}>
                <div className={`l-grid__col l-grid__col--m-6-12 l-grid__col--xl-5-12 c-transition-in`} ref={textRevealRef}>
                    <div className="l-bottom-space">
                        {block.heading &&
                            <h2 className="c-heading c-heading--reverse c-estimate-one-splash__heading" style={{maxWidth: "13em"}}>
                                <SpecialText text={block.heading} />
                            </h2>
                        }
                        {block.text &&
                            <div className="c-estimate-one-splash__text">
                                <div className="scope-text scope-text--estimate-one-splash" dangerouslySetInnerHTML={{__html:`<p>${block.text}</p>`}} />
                            </div>
                        }
                    </div>
                </div>
            </div>
            <div className={`l-grid c-estimate-one-splash__content c-estimate-one-splash__content--bottom`}>
                <div className={`c-estimate-one-splash__image l-grid__col l-grid__col--m-6-12 l-grid__col--m-push-6-12 c-transition-in`} ref={buttonsRevealRef}>
                    {block.buttons && block.buttons.length > 0 &&
                        <ul className="c-estimate-one-splash__buttons l-bottom-space">
                            {block.buttons.map((button, index) => (
                                <li className="c-estimate-one-splash__buttons__item" key={index}>
                                    <SmartLink link={button.button_url} className="c-estimate-one-splash__buttons__button c-button--estimate-one-splash">
                                        <span dangerouslySetInnerHTML={{__html:button.button_text}} />
                                        <svg viewBox="0 0 22 10"><polygon points="16.93 .91 16.25 1.64 19.33 4.5 0 4.5 0 5.5 19.33 5.5 16.25 8.36 16.93 9.09 21.33 5 16.93 .91" /></svg>
                                    </SmartLink>
                                </li>
                            ))}
                        </ul>
                    }
                </div>
            </div>
            
        </div>
    );
}